import type { ChannelClient } from '@hibro/platform-core';
import { createServiceProxy } from '@hibro/platform-core';
import type { BundleInstallBridge } from '@hibro/platform-renderer';
import type {
  BundleArchiveBlob,
  BundleTreeFile,
  IBundleService,
  InstalledModuleInfo,
} from '@hibro/shared';
import { BUNDLE_CHANNEL } from '@hibro/shared';

/**
 * Page-side picker for local install (见 12 / 18). Injected only by the
 * browser runtime: the click that starts `installLocal` must still be live
 * when the picker opens, so this cannot go through the SharedWorker first.
 */
export type PickLocalBundle = (
  kind: 'archive' | 'dir',
) => Promise<
  | { kind: 'archive'; archive: BundleArchiveBlob }
  | { kind: 'dir'; files: readonly BundleTreeFile[] }
  | null
>;

/**
 * 用扩展宿主的安装服务搭出设置页要的桥（见 12）。
 *
 * 与 `detach-bridge.ts` 同形、同理由：账本落进宿主之后，这段代码在桌面和浏览器上是同一份——
 * 后者根本没有 preload。它取代了此前的 `window.hibroBundle` IPC 包装，于是渲染端与模块端
 * （`context.bundles`）打的是同一个后端、同一本账。
 *
 * 中间那层形状转换（`InstalledModuleInfo` ↔ `{ manifest }`、`{ removed }`）是留给并存期的：
 * 设置页 UI 这轮一行不改，等有了模块侧的模块管理界面再连同旧形状一起退场。
 */
export function createBundleBridge(
  client: ChannelClient,
  options: { pickLocal?: PickLocalBundle } = {},
): BundleInstallBridge {
  const service = createServiceProxy<IBundleService>(client, BUNDLE_CHANNEL);
  const pickLocal = options.pickLocal;
  const asManifest = (
    info: InstalledModuleInfo | undefined,
  ): { manifest: { id: string; version: string } } => {
    if (!info) {
      // driver 回 undefined 意为「这个运行端装不了」；宿主已经记过 warn，这里给用户一句话。
      throw new Error('module installation is unavailable on this runtime');
    }
    return { manifest: { id: info.id, version: info.version } };
  };

  return {
    canInstall: () => service.canInstall(),
    // A page-side picker is enough to offer the buttons, even though the
    // host's driver has no `installLocal` (it cannot open a dialog).
    canInstallLocal: () =>
      pickLocal ? Promise.resolve(true) : service.canInstallLocal(),
    install: async (source) =>
      asManifest(
        await service.install({ url: source.url, sha256: source.sha256 }),
      ),
    installLocal: async (kind) => {
      if (pickLocal) {
        const picked = await pickLocal(kind);
        if (!picked) {
          return null;
        }
        const info =
          picked.kind === 'archive'
            ? await service.installLocalArchive(picked.archive)
            : await service.installLocalDirectory(picked.files);
        return asManifest(info);
      }
      const info = await service.installLocal(kind);
      // null = 用户取消了选择器，是个正常结局，不该变成报错。
      return info === null ? null : asManifest(info);
    },
    listInstalled: async () => (await service.state()).installed,
    uninstall: async (moduleId, options) => {
      await service.uninstall(moduleId, options);
      // 旧形状回的是被删掉的缓存目录名，纯展示且当前无人读；账目的真源已经是宿主的广播。
      return { removed: [] };
    },
  };
}
